import React, { useEffect, useState } from "react";

/**
 * SmartImage — optimized image with:
 * - lazy loading (only loads when scrolled near)
 * - async decoding (no main thread blocking)
 * - automatic Unsplash/Pexels resize via URL params (when applicable)
 * - smooth fade-in on load
 * - always positioned absolute to respect parent's aspect ratio (parent must be `relative`)
 */
const SmartImage = ({ src, alt = "", className = "", width = 800, eager = false, ...rest }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => { setLoaded(false); }, [src]);

  let optimizedSrc = src || "";
  try {
    if (optimizedSrc) {
      const u = new URL(optimizedSrc, window.location.origin);
      if (u.hostname.includes("unsplash.com") || u.hostname.includes("images.unsplash.com")) {
        u.searchParams.set("w", String(width));
        u.searchParams.set("q", "75");
        u.searchParams.set("auto", "format");
        optimizedSrc = u.toString();
      } else if (u.hostname.includes("pexels.com")) {
        u.searchParams.set("auto", "compress");
        u.searchParams.set("cs", "tinysrgb");
        u.searchParams.set("w", String(width));
        optimizedSrc = u.toString();
      } else if (u.pathname.startsWith("/api/files/")) {
        // Our own storage: backend supports ?w= for on-the-fly resize
        u.searchParams.set("w", String(width));
        optimizedSrc = u.pathname + "?" + u.searchParams.toString();
      }
    }
  } catch {}

  // Ensure absolute positioning if className doesn't already set it
  const hasAbsolute = className.includes("absolute");
  const finalClass = hasAbsolute
    ? className
    : `absolute inset-0 w-full h-full object-cover ${className}`;

  return (
    <>
      <div
        className={`absolute inset-0 bg-[#0f0f0f] transition-opacity duration-300 ${loaded ? "opacity-0" : "opacity-100 animate-pulse"}`}
        aria-hidden="true"
      />
      <img
        key={optimizedSrc}
        src={optimizedSrc}
        alt={alt}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
        className={`${finalClass} ${loaded ? "opacity-100" : "opacity-0"} transition-opacity duration-500`}
        {...rest}
      />
    </>
  );
};

export default SmartImage;
